import React, { FC, useCallback, useContext } from 'react';
import { Button, TextInput, Loader, RadioButton, Icon } from '@gravity-ui/uikit';
import Plus from '@gravity-ui/icons/Plus';
import Xmark from '@gravity-ui/icons/Xmark';
import i18n from '@yandex-int/i18n';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import * as keyset from 'i18n/FeatureFlags';

import styles from './FeatureFlags.module.scss';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { hasSpecialChars } from 'client/utils/settings/common';

const t = i18n(keyset);

const BOOLEAN_FLAGS = [
    { value: 'True', content: 'True' },
    { value: 'False', content: 'False' },
];

const SkillFlagInput: FC<{
    index: number;
    onInputChange: (flagIndex: number, updatedFlag: string | null) => void;
    value?: string;
}> = (props) => {
    const { index, onInputChange, value } = props;
    const onChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const text = e.currentTarget.value;
            if (hasSpecialChars(text)) return;
            onInputChange(index, text);
        },
        [index, onInputChange],
    );
    return <TextInput value={value} onChange={onChange} size="l" />;
};

const UserFlagKeyInput: FC<{
    index: number;
    flagValue: boolean;
    onUpdateUserFlag: (flagIndex: number, updatedFlagKey: string | null, updatedFlagValue: boolean) => void;
    value?: string;
}> = (props) => {
    const { index, flagValue, onUpdateUserFlag, value } = props;
    const onChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const text = e.currentTarget.value;
            if (hasSpecialChars(text)) return;
            onUpdateUserFlag(index, text, flagValue);
        },
        [index, flagValue, onUpdateUserFlag],
    );
    return <TextInput value={value} onChange={onChange} size="l" />;
};

export const FeatureFlags: React.FC = () => {
    const context = useContext(CurrentAliceAppContext);
    const { isLoading, skillFlags, addSkillFlag, updateSkillFlag, userFlags, addUserFlag, updateUserFlag, saveFlags } =
        useFeatureFlags();

    return (
        <>
            <header className={styles.header}>
                <div>
                    <h1 className={styles.title_main}>{context?.aliceApp?.draft?.name}</h1>
                </div>
                <Button
                    className={styles.saveButton}
                    onClick={saveFlags}
                    view="action"
                    loading={isLoading}
                    disabled={isLoading}
                >
                    {t('save-flags')}
                </Button>
            </header>
            <div className={styles.content}>
                <h3 className={styles.sectionDescription}>{t('app-flags-subtitle')}</h3>
                {isLoading ? (
                    <div className={styles.loaderContainer}>
                        <Loader size="l" />
                    </div>
                ) : (
                    <>
                        <h2 className={styles.title_section}> {t('title-app-flags')} </h2>
                        {skillFlags.map((flag, index) => (
                            <div key={String(index)} className={styles.formRow}>
                                <SkillFlagInput value={flag} index={index} onInputChange={updateSkillFlag} />
                                <div>
                                    <Button onClick={() => updateSkillFlag(index, null)} size="l">
                                        <Icon data={Xmark} />
                                    </Button>
                                </div>
                            </div>
                        ))}
                        <div>
                            <Button className={styles.addButton} onClick={addSkillFlag}>
                                <Icon data={Plus} />
                            </Button>
                        </div>

                        <h2 className={styles.title_section}> {t('title-user-feature-flags')} </h2>
                        {userFlags.map(({ key, value }, index) => (
                            <div key={index} className={styles.formRow}>
                                <UserFlagKeyInput
                                    // theme="normal"
                                    // size="s"
                                    value={key}
                                    index={index}
                                    flagValue={value}
                                    onUpdateUserFlag={updateUserFlag}
                                />
                                <RadioButton
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const updatedValue = e.currentTarget.value;
                                        updateUserFlag(index, key, updatedValue === 'True');
                                    }}
                                    defaultValue={BOOLEAN_FLAGS[0].value}
                                    options={BOOLEAN_FLAGS}
                                    value={value ? 'True' : 'False'}
                                    size="l"
                                />
                                <Button onClick={() => updateUserFlag(index, null, value)} size="l">
                                    <Icon data={Xmark} />
                                </Button>
                            </div>
                        ))}
                        <div>
                            <Button className={styles.addButton} onClick={addUserFlag}>
                                <Icon data={Plus} />
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};
